"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { safeGet, safeRemove, safeSet } from "@/components/onboarding/safe-storage";
import { getUserScopedKey } from "@/lib/user-scoped-storage";
import { coerceStoredSlip, EMPTY_SLIP, SLIP_STORAGE_KEY, SLIP_STORAGE_VERSION, type SlipLeg } from "@/lib/fantasy-props/slip";

export type FantasySlip = {
  readonly legs: readonly SlipLeg[];
  readonly target: string | null;
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly has: (rowId: string) => boolean;
  readonly toggle: (leg: SlipLeg, openOnAdd?: boolean) => void;
  readonly setTarget: (id: string) => void;
  readonly load: (legs: readonly SlipLeg[], target: string) => void;
  readonly remove: (...rowIds: string[]) => () => void;
  readonly clear: () => void;
};

export function useFantasySlip({ userId, book }: { userId: string | undefined; book: string | null }): FantasySlip {
  const storageKey = userId && book !== null ? getUserScopedKey(userId, `${SLIP_STORAGE_KEY}:${book}`) : null;
  const [legs, setLegs] = useState<SlipLeg[]>([]);
  const [target, setTarget] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const hydratedKeyRef = useRef<string | null>(null);
  const legsRef = useRef<SlipLeg[]>(legs);
  legsRef.current = legs;

  useEffect(() => {
    if (storageKey === null || book === null) {
      hydratedKeyRef.current = null;
      setLegs([]);
      setTarget(null);
      return;
    }
    if (hydratedKeyRef.current === storageKey) return;
    hydratedKeyRef.current = storageKey;
    const raw = safeGet(storageKey);
    let restored = EMPTY_SLIP;
    if (raw) {
      try {
        restored = coerceStoredSlip(JSON.parse(raw), book);
      } catch {
        restored = EMPTY_SLIP;
      }
    }
    setLegs(restored.legs);
    setTarget(restored.target);
    setOpen(restored.legs.length > 0);
  }, [storageKey, book]);

  useEffect(() => {
    if (storageKey === null || hydratedKeyRef.current !== storageKey) return;
    if (legs.length === 0 && target === null) {
      safeRemove(storageKey);
      return;
    }
    safeSet(storageKey, JSON.stringify({ v: SLIP_STORAGE_VERSION, legs, target }));
  }, [storageKey, legs, target]);

  const ids = useMemo(() => new Set(legs.map((leg) => leg.rowId)), [legs]);
  const has = useCallback((rowId: string) => ids.has(rowId), [ids]);

  const toggle = useCallback((leg: SlipLeg, openOnAdd = true) => {
    setLegs((current) => (current.some((entry) => entry.rowId === leg.rowId) ? current.filter((entry) => entry.rowId !== leg.rowId) : [...current, leg]));
    if (openOnAdd) setOpen(true);
  }, []);

  const load = useCallback((next: readonly SlipLeg[], nextTarget: string) => {
    setLegs([...next]);
    setTarget(nextTarget);
    setOpen(true);
  }, []);

  const remove = useCallback((...rowIds: string[]) => {
    const before = legsRef.current;
    const after = before.filter((entry) => !rowIds.includes(entry.rowId));
    legsRef.current = after;
    setLegs(after);
    return () => {
      if (legsRef.current !== after) return;
      legsRef.current = before;
      setLegs(before);
      setOpen(true);
    };
  }, []);

  const clear = useCallback(() => {
    setLegs([]);
    setTarget(null);
  }, []);

  return useMemo(
    () => ({ legs, target, open, setOpen, has, toggle, setTarget, load, remove, clear }),
    [legs, target, open, has, toggle, load, remove, clear],
  );
}
